import React, { useEffect } from 'react';
import { Phone, ChevronRight } from 'lucide-react';
/* ── tiny visual building-blocks ── */
function Toggle() {
  return <div className="flex flex-col items-center gap-1" data-id="element-1090">
      <div className="w-1 h-1 rounded-full bg-warmGray/40" data-id="element-1091" />
      <div className="w-2.5 h-6 bg-warmGray/70 rounded-[2px]" data-id="element-1092" />
      <div className="w-1 h-1 rounded-full bg-warmGray/40" data-id="element-1093" />
    </div>;
}
function Outlet() {
  return <div className="flex flex-col items-center justify-center gap-2.5" data-id="element-1094">
      <div className="flex flex-col items-center gap-[3px]" data-id="element-1095">
        <div className="w-1.5 h-3 bg-warmGray/60 rounded-[1px]" data-id="element-1096" />
        <div className="w-1.5 h-3 bg-warmGray/60 rounded-[1px]" data-id="element-1097" />
      </div>
      <div className="flex flex-col items-center gap-[3px]" data-id="element-1098">
        <div className="w-1.5 h-3 bg-warmGray/60 rounded-[1px]" data-id="element-1099" />
        <div className="w-1.5 h-3 bg-warmGray/60 rounded-[1px]" data-id="element-1100" />
      </div>
    </div>;
}
function Decora() {
  return <div className="flex flex-col items-center gap-1" data-id="element-1101">
      <div className="w-1 h-1 rounded-full bg-warmGray/40" data-id="element-1102" />
      <div className="w-6 h-10 border-2 border-warmGray/50 rounded-[3px]" data-id="element-1103" />
      <div className="w-1 h-1 rounded-full bg-warmGray/40" data-id="element-1104" />
    </div>;
}
/* ── plate wrapper ── */
function Plate({
  label,
  dimensions,
  children
}: {
  label: string;
  dimensions?: string;
  children: React.ReactNode;
}) {
  return <div className="flex flex-col items-center gap-3" data-id="element-1105">
      <div className="bg-cream-dark border border-warmBorder rounded-[4px] px-4 py-5 flex items-center justify-center gap-3 min-w-[60px] shadow-sm" data-id="element-1106">
        {children}
      </div>
      <div className="text-center" data-id="element-1107">
        <p className="text-sm font-bold text-charcoal leading-tight" data-id="element-1108">{label}</p>
        {dimensions && <p className="text-xs text-warmGray mt-0.5" data-id="element-1109">{dimensions}</p>}
      </div>
    </div>;
}
/* ── section wrapper ── */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="mb-10 last:mb-0" data-id="element-1110">
      <h3 className="text-lg font-serif text-charcoal mb-5 pb-2 border-b border-warmBorder" data-id="element-1111">
        {title}
      </h3>
      <div className="flex flex-wrap gap-6 md:gap-8" data-id="element-1112">{children}</div>
    </div>;
}
/* ── page ── */
export function SizeChartPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <section className="bg-cream" data-id="element-1113">
      {/* Page Header */}
      <div className="bg-cream-dark border-b border-warmBorder" data-id="element-1114">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-id="element-1115">
          <nav className="flex items-center gap-2 text-sm text-warmGray mb-4" data-id="element-1116">
            <a href="#/" className="hover:text-gold transition-colors" data-id="element-1117">
              Home
            </a>
            <ChevronRight size={14} className="text-warmGray/50" data-id="element-1118" />
            <span className="text-charcoal font-medium" data-id="element-1119">Size Chart</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-serif text-charcoal" data-id="element-1120">
            Switchplate Sizes &amp; Configurations
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-1121">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16" data-id="element-1122">
          {/* Main Content */}
          <div className="lg:w-2/3" data-id="element-1123">
            <div className="bg-white p-6 md:p-10 rounded-sm shadow-sm border border-warmBorder mb-10" data-id="element-1124">
              {/* Standard Toggles */}
              <Section title="Standard Toggle Switches" data-id="element-1125">
                <Plate label="Single Switch" dimensions='3½" × 5"' data-id="element-1126">
                  <Toggle data-id="element-1127" />
                </Plate>
                <Plate label="Double Switch" dimensions='4⅞" × 5"' data-id="element-1128">
                  <Toggle data-id="element-1129" />
                  <Toggle data-id="element-1130" />
                </Plate>
                <Plate label="Triple Switch" dimensions='6⅜" × 5"' data-id="element-1131">
                  <Toggle data-id="element-1132" />
                  <Toggle data-id="element-1133" />
                  <Toggle data-id="element-1134" />
                </Plate>
                <Plate label="Quad Switch" dimensions='8⅜" × 5"' data-id="element-1135">
                  <Toggle data-id="element-1136" />
                  <Toggle data-id="element-1137" />
                  <Toggle data-id="element-1138" />
                  <Toggle data-id="element-1139" />
                </Plate>
              </Section>

              {/* Large Toggles */}
              <Section title="Large Toggle Switches" data-id="element-1140">
                <Plate label="Five Switch" data-id="element-1141">
                  <Toggle data-id="element-1142" />
                  <Toggle data-id="element-1143" />
                  <Toggle data-id="element-1144" />
                  <Toggle data-id="element-1145" />
                  <Toggle data-id="element-1146" />
                </Plate>
                <Plate label="Six Switch" data-id="element-1147">
                  <Toggle data-id="element-1148" />
                  <Toggle data-id="element-1149" />
                  <Toggle data-id="element-1150" />
                  <Toggle data-id="element-1151" />
                  <Toggle data-id="element-1152" />
                  <Toggle data-id="element-1153" />
                </Plate>
              </Section>

              {/* Receptacles */}
              <Section title="Receptacles (Outlets)" data-id="element-1154">
                <Plate label="Receptacle" dimensions="2 outlets" data-id="element-1155">
                  <Outlet data-id="element-1156" />
                </Plate>
                <Plate label="Double Receptacle" dimensions="4 outlets" data-id="element-1157">
                  <Outlet data-id="element-1158" />
                  <Outlet data-id="element-1159" />
                </Plate>
              </Section>

              {/* Combo Plates */}
              <Section title="Combination Plates" data-id="element-1160">
                <Plate label="Combo 1" data-id="element-1161">
                  <Toggle data-id="element-1162" />
                  <Outlet data-id="element-1163" />
                </Plate>
                <Plate label="Combo 2" data-id="element-1164">
                  <Toggle data-id="element-1165" />
                  <Toggle data-id="element-1166" />
                  <Outlet data-id="element-1167" />
                </Plate>
                <Plate label="Combo 3" data-id="element-1168">
                  <Toggle data-id="element-1169" />
                  <Toggle data-id="element-1170" />
                  <Toggle data-id="element-1171" />
                  <Outlet data-id="element-1172" />
                </Plate>
                <Plate label="Combo 4" data-id="element-1173">
                  <Toggle data-id="element-1174" />
                  <Toggle data-id="element-1175" />
                  <Toggle data-id="element-1176" />
                  <Toggle data-id="element-1177" />
                  <Outlet data-id="element-1178" />
                </Plate>
                <Plate label="Combo 5" data-id="element-1179">
                  <Toggle data-id="element-1180" />
                  <Toggle data-id="element-1181" />
                  <Outlet data-id="element-1182" />
                  <Outlet data-id="element-1183" />
                </Plate>
                <Plate label="Combo 6" data-id="element-1184">
                  <Toggle data-id="element-1185" />
                  <Toggle data-id="element-1186" />
                  <Toggle data-id="element-1187" />
                  <Outlet data-id="element-1188" />
                  <Outlet data-id="element-1189" />
                </Plate>
              </Section>

              {/* Decora / Rocker / GFCI */}
              <Section title="Decora / Rocker / GFCI" data-id="element-1190">
                <Plate label="Decora Single" data-id="element-1191">
                  <Decora data-id="element-1192" />
                </Plate>
                <Plate label="Decora Double" data-id="element-1193">
                  <Decora data-id="element-1194" />
                  <Decora data-id="element-1195" />
                </Plate>
                <Plate label="Decora Triple" data-id="element-1196">
                  <Decora data-id="element-1197" />
                  <Decora data-id="element-1198" />
                  <Decora data-id="element-1199" />
                </Plate>
              </Section>

              {/* Decora Combos */}
              <Section title="Decora / GFCI Combination Plates" data-id="element-1200">
                <Plate label="GFCI Combo 1" data-id="element-1201">
                  <Toggle data-id="element-1202" />
                  <Decora data-id="element-1203" />
                </Plate>
                <Plate label="GFCI Combo 2" data-id="element-1204">
                  <Toggle data-id="element-1205" />
                  <Toggle data-id="element-1206" />
                  <Decora data-id="element-1207" />
                </Plate>
                <Plate label="GFCI Combo 3" data-id="element-1208">
                  <Toggle data-id="element-1209" />
                  <Toggle data-id="element-1210" />
                  <Toggle data-id="element-1211" />
                  <Decora data-id="element-1212" />
                </Plate>
                <Plate label="GFCI Combo 4" data-id="element-1213">
                  <Decora data-id="element-1214" />
                  <Toggle data-id="element-1215" />
                  <Toggle data-id="element-1216" />
                </Plate>
                <Plate label="GFCI Combo 5" data-id="element-1217">
                  <Decora data-id="element-1218" />
                  <Outlet data-id="element-1219" />
                </Plate>
                <Plate label="GFCI Combo 6" data-id="element-1220">
                  <Decora data-id="element-1221" />
                  <Decora data-id="element-1222" />
                  <Toggle data-id="element-1223" />
                </Plate>
              </Section>
            </div>

            {/* Don't See It Here? CTA */}
            <div className="bg-charcoal text-cream p-8 md:p-10 rounded-sm" data-id="element-1224">
              <h3 className="text-2xl font-serif text-white mb-3" data-id="element-1225">
                Don't See Your Size?
              </h3>
              <p className="text-warmGray-light leading-relaxed mb-6 text-lg" data-id="element-1226">
                Need blanks, switch with blank, despards, or other
                configurations? Many other options are available by Special
                Order.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1227">
                <a href="#/custom-orders" className="inline-flex justify-center items-center px-8 py-3.5 bg-gold hover:bg-gold-light text-white font-bold rounded-sm transition-colors text-sm uppercase tracking-wider" data-id="element-1228">
                  View Special Order Sizes
                </a>
                <a href="tel:8004976946" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/10 border border-white/20 text-white hover:bg-white/20 font-bold rounded-sm transition-colors text-sm" data-id="element-1229">
                  <Phone size={16} data-id="element-1230" />
                  800.497.6946
                </a>
              </div>
              <p className="text-warmGray-light text-sm mt-4" data-id="element-1231">
                Available 9AM–5PM EST
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3" data-id="element-1232">
            <div className="bg-white p-8 rounded-sm border border-warmBorder sticky top-32 shadow-warm" data-id="element-1233">
              <h4 className="font-serif text-xl text-charcoal mb-6 border-b border-warmBorder pb-4" data-id="element-1234">
                Helpful Links
              </h4>
              <ul className="space-y-4" data-id="element-1235">
                <li data-id="element-1236">
                  <a href="#/size-chart" className="flex items-center text-gold font-medium group" data-id="element-1237">
                    <span className="w-2 h-2 rounded-full bg-gold mr-3" data-id="element-1238" />
                    Size Chart &amp; Configurations
                  </a>
                </li>
                <li data-id="element-1239">
                  <a href="#/custom-orders" className="flex items-center text-warmGray hover:text-gold transition-colors font-medium group" data-id="element-1240">
                    <span className="w-2 h-2 rounded-full bg-gold/50 mr-3 group-hover:bg-gold transition-colors" data-id="element-1241" />
                    Custom &amp; Special Orders
                  </a>
                </li>
                <li data-id="element-1242">
                  <a href="#/faq" className="flex items-center text-warmGray hover:text-gold transition-colors font-medium group" data-id="element-1243">
                    <span className="w-2 h-2 rounded-full bg-gold/50 mr-3 group-hover:bg-gold transition-colors" data-id="element-1244" />
                    FAQ &amp; "How To" Info
                  </a>
                </li>
                <li data-id="element-1245">
                  <a href="#/shipping" className="flex items-center text-warmGray hover:text-gold transition-colors font-medium group" data-id="element-1246">
                    <span className="w-2 h-2 rounded-full bg-gold/50 mr-3 group-hover:bg-gold transition-colors" data-id="element-1247" />
                    Shipping Information
                  </a>
                </li>
                <li data-id="element-1248">
                  <a href="#/contact" className="flex items-center text-warmGray hover:text-gold transition-colors font-medium group" data-id="element-1249">
                    <span className="w-2 h-2 rounded-full bg-gold/50 mr-3 group-hover:bg-gold transition-colors" data-id="element-1250" />
                    Contact Us
                  </a>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-warmBorder" data-id="element-1251">
                <p className="text-sm text-warmGray mb-2 uppercase tracking-wider font-bold" data-id="element-1252">
                  Need sizing help?
                </p>
                <a href="tel:8004976946" className="text-2xl font-serif text-gold hover:text-gold-dark transition-colors" data-id="element-1253">
                  800.497.6946
                </a>
                <p className="text-xs text-warmGray mt-1" data-id="element-1254">
                  Available 9AM–5PM EST
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}